import { Cookie } from 'core-events/components';
import {
    RecaptchaFallbackDecision,
    RecaptchaV3Action,
    UseFormWithRecaptcha,
    useFormWithRecaptcha,
} from 'google-recaptcha/components';
import { useCallback } from 'react';

import { LoggerFactory } from '@videoblocks/typescript-logger';

import { getCookie } from '../../../components/lib/cookies/getCookie';
import { validatePassword } from '../../../lib/validation/passwordValidation';
import { useSignUpContext } from './SignUpContext';
import { signUpAction } from './signUpAction';

const logger = LoggerFactory.create('useSubmitSignUpForm');
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Wires the credential sign-up submission (validation + signUpAction + error handling) into the
// shared reCAPTCHA fallback hook. Returns the hook result directly; the form wraps itself in a
// RecaptchaFormProvider so <RecaptchaFormChallenge/> reads it from context.
export function useSubmitSignUpForm(planCode?: string, redirectAfterRegister?: string): UseFormWithRecaptcha {
    const { setError, setSubmitText } = useSignUpContext();

    const onSubmit = useCallback(
        async (
            formData: FormData,
            recaptcha: { token: string | undefined; version: 'v2' | 'v3' },
        ): Promise<RecaptchaFallbackDecision> => {
            const email = formData.get('email') as string;
            const password = formData.get('password') as string;

            if (!EMAIL_REGEX.test(email)) {
                logger.error('Invalid email format');
                setError('Please enter a valid email address.');
                return RecaptchaFallbackDecision.Handled;
            }

            const validation = validatePassword(password);
            if (!validation.isValid) {
                logger.error(validation.errors[0]);
                setError(validation.errors[0]);
                return RecaptchaFallbackDecision.Handled;
            }

            setSubmitText('Processing...');
            const result = await signUpAction({
                redirectAfterRegister,
                uniqueId: getCookie(Cookie.Vid),
                email,
                password,
                planCode,
                signupType: 'email_and_password',
                recaptchaToken: recaptcha.token,
                recaptchaVersion: recaptcha.version,
            });

            if (result && 'requiresV2Challenge' in result) {
                setSubmitText('Create Account');
                if (result.error) {
                    setError(result.error);
                }
                return RecaptchaFallbackDecision.NeedsV2Challenge;
            }

            if (result?.error) {
                // The reCAPTCHA passed but account creation failed (e.g. email already registered);
                // the v2 token is spent, so refresh the checkbox for the retry.
                setError(result.error);
                setSubmitText('Create Account');
                return RecaptchaFallbackDecision.RefreshV2Challenge;
            }
            return RecaptchaFallbackDecision.Handled;
        },
        [setError, setSubmitText, planCode, redirectAfterRegister],
    );

    const onError = useCallback(() => {
        setError('An unexpected error occurred. Please try again.');
        setSubmitText('Create Account');
    }, [setError, setSubmitText]);

    return useFormWithRecaptcha({
        action: RecaptchaV3Action.SIGN_UP_WITH_CREDENTIALS,
        onSubmit,
        onError,
        onValidationError: setError,
    });
}
